/**
 * `/local/:id` — ローカル CPU 対戦のホスト画面。
 *
 * - localStorage の setup を読んで `useRoom.connectLocal` で起動
 * - phase === 'playing' / 'finished' に応じて Play / Result を出すだけ
 * - WS は使わないので 「サーバーに接続中…」 状態は瞬時に抜ける
 */

import { useEffect, useState } from 'react';
import { LoadingScreen } from '../components/Loading';
import type { MatchConfig } from '../proto';
import { useRoom } from '../state/room';
import { useSession } from '../state/session';
import { ensureWasm } from '../wasm';
import { type CpuLevel, ensureCpuWasm } from '../wasm/cpu';
import { Play } from './Play';
import { Result } from './Result';

interface StoredSetup {
  config: MatchConfig;
  cpuLevel: CpuLevel;
  yourRolePreference: 'random' | 'host' | 'guest';
}

export function LocalRoom({ id }: { id: string }) {
  const phase = useRoom((s) => s.phase);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // mount 時に 1 回だけ connect / unmount 時に disconnect。 ストア関数は `getState()` 経由で
  // 参照することで dep にしなくて済む (Zustand なら関数 ref は安定だが biome は知らないため)。
  // me?.handle も `getState()` の時点の最新値を使う (= ユーザー名を mid-game に変えても
  // 既存 match の表示は最初の選択のまま、 という挙動が自然なので問題なし)。
  useEffect(() => {
    let cancelled = false;
    void Promise.all([ensureWasm(), ensureCpuWasm()])
      .then(() => {
        if (cancelled) return;
        const raw = localStorage.getItem(`mixed-game.local.setup.${id}`);
        if (!raw) {
          setError('対戦設定が見つかりません。 設定からやり直してください。');
          return;
        }
        const stored = JSON.parse(raw) as StoredSetup;
        const meHandle = useSession.getState().me?.handle ?? 'あなた';
        useRoom.getState().connectLocal({
          id,
          config: stored.config,
          cpuLevel: stored.cpuLevel,
          yourRolePreference: stored.yourRolePreference,
          yourName: meHandle,
          // CPU 対戦は「<handle> vs Bot」 と見えるよう、 相手は "Bot" 固定。
          cpuName: 'Bot',
        });
        setReady(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(`WASM 初期化に失敗: ${String(e)}`);
      });
    return () => {
      cancelled = true;
      useRoom.getState().disconnect();
    };
  }, [id]);

  // phase が 'connecting' のまま 3 秒経過したら 「読み込み失敗」 として扱う
  // (固まり防止のウォッチドッグ — 通常は connectLocal 同期 broadcast で即 'playing' になる)
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    if (!ready || phase !== 'connecting') {
      setStuck(false);
      return;
    }
    const t = setTimeout(() => setStuck(true), 3000);
    return () => clearTimeout(t);
  }, [ready, phase]);

  if (error) {
    return (
      <main className="centered">
        <div className="status-card">
          <p className="error">{error}</p>
          <a className="btn-secondary" href="/local/new">
            設定に戻る
          </a>
        </div>
      </main>
    );
  }

  if (stuck) {
    return (
      <main className="centered">
        <div className="status-card">
          <p className="error">読み込みに時間がかかっています。 ページを再読み込みしてください。</p>
          <button className="btn-secondary" onClick={() => location.reload()}>再読み込み</button>
          <a className="btn-secondary" href="/local/new">
            設定に戻る
          </a>
        </div>
      </main>
    );
  }

  if (!ready || phase === 'connecting' || phase === 'closed' || phase === 'lobby') {
    return <LoadingScreen label="準備中" />;
  }

  if (phase === 'playing') return <Play code={id} />;
  if (phase === 'finished') return <Result />;

  return (
    <main className="centered">
      <p>状態不明: {phase}</p>
    </main>
  );
}
